<template>
  <div>
    <CheckoutSessionAddonCardSkeleton v-if="isLoading" />
    <HeaderBodyCard v-else>
      <template #header>
        <div class="is-flex is-justify-content-space-between is-size-2 has-text-weight-semibold">
          <div>{{ description.title }}</div>
          <div
            class="whitespace-no-wrap"
            :data-e2e="BillingCypressAttribute.checkoutSession.views.ADDON_CARD_PRICE"
          >
            {{ formattedPrice }}
          </div>
        </div>
        <div class="has-text-weight-semibold is-uppercase">{{ description.subtitle }}</div>
      </template>
      <template #body>
        <BillingCardInclusionsExclusions
          :inclusions="description.inclusions"
          :exclusions="description.exclusions"
          class="mt-0"
        />
        <div
          v-if="showLegalDisclaimer"
          class="has-background-light has-border-radius my-4 py-5 px-4"
          v-dompurify-html="description.legalDisclaimer"
        />
        <div
          v-if="hasControls"
          class="is-flex is-justify-content-flex-end is-align-items-center button-section"
        >
          <ButtonView
            v-if="!hasProductKey"
            type="is-primary is-outlined"
            @click="add"
            :data-e2e="BillingCypressAttribute.buttons.ADD_PRODUCT"
          >
            +&nbsp;{{ $t('core.add') }}
          </ButtonView>
          <Banner
            v-else
            type="is-success"
            :title="$t('billing.added-to-your-cart')"
            title-text-size="is-size-5"
            class="is-full-width"
            custom-icon="check-circle-1-filled"
          >
            <template #action-button>
              <ButtonView
                type="is-primary is-light"
                @click="remove"
                class="remove-button"
                :data-e2e="BillingCypressAttribute.buttons.REMOVE_PRODUCT"
              >
                {{ $t('core.remove') }}
              </ButtonView>
            </template>
          </Banner>
        </div>
      </template>
    </HeaderBodyCard>
  </div>
</template>
<script>
import { FormComputedValueMixin } from '@/modules/core/form/mixins/FormComputedValueMixin';
import { CheckoutSessionCart } from '@/modules/billing/models/CheckoutSessionCart';
import HeaderBodyCard from '@/modules/core/app/components/HeaderBodyCard.vue';
import Banner from '@/modules/core/ui/components/Banner.vue';
import pluralize from 'pluralize';
import { BillingCypressAttribute } from '@/modules/billing/constants/BillingCypressAttribute';
import NumeralService from '@/modules/core/app/services/NumeralService';
import { ColumnFormat } from '@/modules/core/app/constants/data.constants';
import { CheckoutSessionProductDescription } from '@/modules/billing/constants/CheckoutSessionProductDescription';
import HubSpotProductService from '@/modules/hubspot/services/HubSpotProductService';
import CheckoutSessionAddonCardSkeleton from '@/modules/billing/components/flow/standardplan/skeletons/CheckoutSessionAddonCardSkeleton.vue';
import StripeCouponService from '@/modules/billing/services/stripe/StripeCouponService';
import BillingCardInclusionsExclusions from '@/modules/estate/components/presentational/BillingCardInclusionsExclusions.vue';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'CheckoutSessionAddonCard',
  mixins: [FormComputedValueMixin],
  components: {
    ButtonView,
    BillingCardInclusionsExclusions,
    CheckoutSessionAddonCardSkeleton,
    Banner,
    HeaderBodyCard
  },
  props: {
    value: {
      type: CheckoutSessionCart
    },
    description: {
      type: CheckoutSessionProductDescription,
      required: true
    },
    showLegalDisclaimer: {
      type: Boolean,
      default: false
    },
    hasControls: {
      type: Boolean,
      default: true
    }
  },
  static: {
    BillingCypressAttribute
  },
  data() {
    return {
      price: null,
      isLoading: true
    };
  },
  computed: {
    hasProductKey() {
      return this.computedValue.productKeys.includes(this.description.productKey);
    },
    formattedPrice() {
      return NumeralService.formatValue(this.price, ColumnFormat.FORMAT_CURRENCY);
    }
  },
  methods: {
    pluralize,
    add() {
      this.computedValue.productKeys.push(this.description.productKey);
    },
    remove() {
      this.computedValue.removeProductKey(this.description.productKey);
    },
    async loadProducts() {
      try {
        const [products, coupon] = await Promise.all([
          HubSpotProductService.getProductsWithKeys([this.description.productKey]),
          StripeCouponService.getPartnershipInvitationCoupon()
        ]);

        this.price = products
          .find((product) => product.propertyProductkey === this.description.productKey)
          ?.getDiscountedPrice(coupon);
      } finally {
        this.isLoading = false;
      }
    }
  },
  mounted() {
    this.loadProducts();
  }
};
</script>

<style scoped>
.button-section {
  padding-top: var(--ce-14px);
  height: var(--ce-60px);
}

:deep(header.message-header) {
  padding: var(--ce-2px) var(--ce-8px);
}

:deep(div.column:has(.remove-button)) {
  padding-right: 0 !important;
}
</style>
